export type ResumableChatApp = 'yochai' | 'virgil' | 'howie';

export type ResumableTurnStatus =
  | 'queued'
  | 'running'
  | 'completed'
  | 'stopped'
  | 'interrupted'
  | 'failed';

export type ResumableChatCommandType =
  | 'sync_turn'
  | 'stop_turn'
  | 'steer_turn'
  | 'edit_and_regenerate';

export interface ResumableTurnReference {
  app: ResumableChatApp;
  surface: string;
  turnId: string;
  clientMessageId: string;
  conversationId: string | null;
  lastSequence: number;
  status: ResumableTurnStatus;
  updatedAt: string;
  /**
   * What the turn was asked about, when the surface is shared by several
   * subjects (Yochai's reader: one stored turn per user for every text, so
   * this is the text slug). A surface that shows another subject must not
   * paint the turn or adopt its conversation.
   */
  scopeKey?: string;
}

export interface ResumableTurnSnapshot {
  type: 'turn_snapshot';
  turn_id: string;
  client_message_id: string;
  conversation_id: string | null;
  status: ResumableTurnStatus;
  user_message: {
    content: string;
    timestamp?: string;
  };
  assistant_content: string;
  last_sequence: number;
  error?: string | null;
}

export interface ResumableTurnEnvelope {
  turn_id: string;
  client_message_id: string;
  sequence?: number;
}

export interface ResumableMessageLike {
  id?: string;
  role?: string;
  content?: string;
  metadata?: Record<string, unknown>;
}

export interface ResumableMessageIdentity {
  role: 'user' | 'assistant';
  durableId?: string | null;
  messageId?: string | null;
  content: string;
}

export const RESUMABLE_MESSAGE_ID_METADATA_KEY = 'resumable_message_id';

export interface ResumableTurnSyncCommand {
  type: 'sync_turn';
  turn_id: string;
  after_sequence: number;
}

export interface ResumableTurnStopCommand {
  type: 'stop_turn';
  turn_id: string;
}

export interface ResumableTurnSteerCommand {
  type: 'steer_turn';
  turn_id: string;
  content: string;
  next_turn_id: string;
  client_message_id: string;
}

export interface ResumableTurnEditCommand {
  type: 'edit_and_regenerate';
  conversation_id: string;
  message_id: string;
  content: string;
  turn_id: string;
  client_message_id: string;
}

export type ResumableChatCommand =
  | ResumableTurnSyncCommand
  | ResumableTurnStopCommand
  | ResumableTurnSteerCommand
  | ResumableTurnEditCommand;

export function buildTurnSyncCommand(
  reference: ResumableTurnReference,
): ResumableTurnSyncCommand {
  return {
    type: 'sync_turn',
    turn_id: reference.turnId,
    after_sequence: reference.lastSequence,
  };
}

export function buildResumableChatSurface(
  userId: string | null | undefined,
  surface: string,
): string {
  return `${userId || 'anonymous'}:${surface}`;
}

export function findResumableMessageIndex(
  messages: readonly ResumableMessageLike[],
  identity: ResumableMessageIdentity,
): number {
  return messages.findIndex((message) => {
    if (message.role !== identity.role) return false;
    if (identity.durableId) {
      if (message.metadata?.[RESUMABLE_MESSAGE_ID_METADATA_KEY] === identity.durableId) {
        return true;
      }
    }
    if (identity.messageId) return message.id === identity.messageId;
    if (identity.durableId) return false;
    return message.content === identity.content;
  });
}

export function resumableAssistantMessageId(
  turnId?: string | null,
  messageId?: string | null,
): string | undefined {
  return messageId || (turnId ? `${turnId}:assistant` : undefined);
}

const ACTIVE_TURN_PREFIX = 'lightning:chat:active-turn';
const DRAFT_PREFIX = 'lightning:chat:draft';
const OUTBOX_PREFIX = 'lightning:chat:outbox';
const QUEUED_FOLLOW_UPS_PREFIX = 'lightning:chat:queued-follow-ups';

function safeStorage(storage: Storage | undefined): Storage | null {
  return storage && typeof storage.getItem === 'function' ? storage : null;
}

function scopedKey(prefix: string, app: ResumableChatApp, surface: string, id?: string | null): string {
  return [prefix, app, surface, id || 'new'].map(encodeURIComponent).join(':');
}

export function activeTurnStorageKey(app: ResumableChatApp, surface: string): string {
  return scopedKey(ACTIVE_TURN_PREFIX, app, surface);
}

export function draftStorageKey(
  app: ResumableChatApp,
  surface: string,
  conversationId?: string | null,
): string {
  return scopedKey(DRAFT_PREFIX, app, surface, conversationId);
}

export function pendingChatCommandStorageKey(app: ResumableChatApp, surface: string): string {
  return scopedKey(OUTBOX_PREFIX, app, surface);
}

export function queuedFollowUpsStorageKey(app: ResumableChatApp, surface: string): string {
  return scopedKey(QUEUED_FOLLOW_UPS_PREFIX, app, surface);
}

export function createResumableTurnReference(
  app: ResumableChatApp,
  surface: string,
  conversationId?: string | null,
): ResumableTurnReference {
  return {
    app,
    surface,
    turnId: crypto.randomUUID(),
    clientMessageId: crypto.randomUUID(),
    conversationId: conversationId || null,
    lastSequence: 0,
    status: 'queued',
    updatedAt: new Date().toISOString(),
  };
}

export function loadActiveTurn(
  app: ResumableChatApp,
  surface: string,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): ResumableTurnReference | null {
  const target = safeStorage(storage);
  if (!target) return null;
  const raw = target.getItem(activeTurnStorageKey(app, surface));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<ResumableTurnReference>;
    if (
      parsed.app !== app
      || parsed.surface !== surface
      || typeof parsed.turnId !== 'string'
      || typeof parsed.clientMessageId !== 'string'
    ) {
      return null;
    }
    return {
      app,
      surface,
      turnId: parsed.turnId,
      clientMessageId: parsed.clientMessageId,
      conversationId: typeof parsed.conversationId === 'string' ? parsed.conversationId : null,
      lastSequence: typeof parsed.lastSequence === 'number' ? parsed.lastSequence : 0,
      status: parsed.status || 'queued',
      updatedAt: parsed.updatedAt || new Date(0).toISOString(),
      ...(typeof parsed.scopeKey === 'string' && parsed.scopeKey ? { scopeKey: parsed.scopeKey } : {}),
    };
  } catch {
    return null;
  }
}

export function saveActiveTurn(
  reference: ResumableTurnReference,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): void {
  const target = safeStorage(storage);
  if (!target) return;
  target.setItem(
    activeTurnStorageKey(reference.app, reference.surface),
    JSON.stringify({ ...reference, updatedAt: new Date().toISOString() }),
  );
}

export function clearActiveTurn(
  app: ResumableChatApp,
  surface: string,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): void {
  safeStorage(storage)?.removeItem(activeTurnStorageKey(app, surface));
}

export function loadPendingChatCommand<T extends Record<string, unknown>>(
  app: ResumableChatApp,
  surface: string,
  storage?: Storage,
): T | null {
  const key = pendingChatCommandStorageKey(app, surface);
  const target = safeStorage(
    storage ?? (typeof sessionStorage === 'undefined' ? undefined : sessionStorage),
  );
  let raw = target?.getItem(key);
  if (!raw && storage === undefined && typeof localStorage !== 'undefined') {
    const legacyStorage = safeStorage(localStorage);
    raw = legacyStorage?.getItem(key);
    if (raw && target) {
      target.setItem(key, raw);
      legacyStorage?.removeItem(key);
    }
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as T : null;
  } catch {
    return null;
  }
}

export function savePendingChatCommand(
  app: ResumableChatApp,
  surface: string,
  command: Record<string, unknown>,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): void {
  safeStorage(storage)?.setItem(
    pendingChatCommandStorageKey(app, surface),
    JSON.stringify(command),
  );
}

export function clearPendingChatCommand(
  app: ResumableChatApp,
  surface: string,
  storage?: Storage,
): void {
  const key = pendingChatCommandStorageKey(app, surface);
  safeStorage(
    storage ?? (typeof sessionStorage === 'undefined' ? undefined : sessionStorage),
  )?.removeItem(key);
  if (storage === undefined && typeof localStorage !== 'undefined') {
    safeStorage(localStorage)?.removeItem(key);
  }
}

/**
 * A follow-up the learner typed while a turn was still running. It has not
 * reached the server: it is held client-side and sent as a plain `message`
 * once the active turn settles. Persisted next to the active turn and the
 * pending command so closing and reopening the chat cannot silently drop it.
 */
export interface PersistedQueuedFollowUp<TPayload = unknown> {
  payload: TPayload;
  turn: ResumableTurnReference;
  /** When the learner typed it; the age bound below is measured from here. */
  queuedAt: string;
  /** The send analytics already fired for this turn; do not report it twice. */
  analyticsReported?: boolean;
}

/**
 * How many held follow-ups survive a remount. The queue flushes in order, so
 * the head is what goes out next and the cap keeps the oldest entries; a queue
 * deeper than this was typed into a conversation the answers have long since
 * moved past. Four also bounds how many unattended turns a single reopen can
 * fire back to back.
 */
export const QUEUED_FOLLOW_UP_MAX_COUNT = 4;

/**
 * How long a held follow-up stays sendable. sessionStorage already dies with
 * the tab, so this only guards a tab left open: a question typed more than an
 * hour ago would arrive as a non-sequitur in a conversation that has moved on,
 * and the learner has no memory of having queued it.
 */
export const QUEUED_FOLLOW_UP_MAX_AGE_MS = 60 * 60 * 1000;

function isPersistedQueuedFollowUp(value: unknown): value is PersistedQueuedFollowUp {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<PersistedQueuedFollowUp>;
  const turn = entry.turn as Partial<ResumableTurnReference> | undefined;
  return Boolean(
    entry.payload
    && typeof entry.payload === 'object'
    && typeof entry.queuedAt === 'string'
    && turn
    && typeof turn.turnId === 'string'
    && typeof turn.clientMessageId === 'string',
  );
}

export function loadQueuedFollowUps<TPayload = unknown>(
  app: ResumableChatApp,
  surface: string,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
  now: number = Date.now(),
): PersistedQueuedFollowUp<TPayload>[] {
  const target = safeStorage(storage);
  const raw = target?.getItem(queuedFollowUpsStorageKey(app, surface));
  if (!raw) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .filter(isPersistedQueuedFollowUp)
    .filter((entry) => {
      const queuedAt = Date.parse(entry.queuedAt);
      return Number.isFinite(queuedAt) && now - queuedAt < QUEUED_FOLLOW_UP_MAX_AGE_MS;
    })
    .slice(0, QUEUED_FOLLOW_UP_MAX_COUNT)
    .map((entry) => ({
      payload: entry.payload as TPayload,
      turn: { ...entry.turn, app, surface },
      queuedAt: entry.queuedAt,
      ...(entry.analyticsReported ? { analyticsReported: true } : {}),
    }));
}

export function saveQueuedFollowUps(
  app: ResumableChatApp,
  surface: string,
  entries: readonly PersistedQueuedFollowUp[],
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): void {
  const target = safeStorage(storage);
  if (!target) return;
  const key = queuedFollowUpsStorageKey(app, surface);
  if (!entries.length) {
    target.removeItem(key);
    return;
  }
  try {
    target.setItem(key, JSON.stringify(entries.slice(0, QUEUED_FOLLOW_UP_MAX_COUNT)));
  } catch {
    // Out of quota (a large inline attachment, say). A stale list is worse
    // than none: it would resurrect messages the live queue has moved past.
    try {
      target.removeItem(key);
    } catch {
      // Storage is unusable; the in-memory queue still carries this session.
    }
  }
}

export function clearQueuedFollowUps(
  app: ResumableChatApp,
  surface: string,
  storage: Storage | undefined = typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
): void {
  safeStorage(storage)?.removeItem(queuedFollowUpsStorageKey(app, surface));
}

export function loadChatDraft(
  app: ResumableChatApp,
  surface: string,
  conversationId?: string | null,
  storage: Storage | undefined = typeof localStorage === 'undefined' ? undefined : localStorage,
): string {
  return safeStorage(storage)?.getItem(draftStorageKey(app, surface, conversationId)) || '';
}

export function saveChatDraft(
  app: ResumableChatApp,
  surface: string,
  conversationId: string | null | undefined,
  value: string,
  storage: Storage | undefined = typeof localStorage === 'undefined' ? undefined : localStorage,
): void {
  const target = safeStorage(storage);
  if (!target) return;
  const key = draftStorageKey(app, surface, conversationId);
  if (value) target.setItem(key, value);
  else target.removeItem(key);
}

export function clearChatDraft(
  app: ResumableChatApp,
  surface: string,
  conversationId?: string | null,
  storage: Storage | undefined = typeof localStorage === 'undefined' ? undefined : localStorage,
): void {
  safeStorage(storage)?.removeItem(draftStorageKey(app, surface, conversationId));
}

export function applyTurnSnapshot(
  reference: ResumableTurnReference,
  snapshot: ResumableTurnSnapshot,
): ResumableTurnReference {
  if (snapshot.turn_id !== reference.turnId) return reference;
  return {
    ...reference,
    conversationId: snapshot.conversation_id || reference.conversationId,
    lastSequence: Math.max(reference.lastSequence, snapshot.last_sequence || 0),
    status: snapshot.status,
    updatedAt: new Date().toISOString(),
  };
}

export function terminalSnapshotAssistantContent(
  snapshot: Pick<ResumableTurnSnapshot, 'status' | 'assistant_content' | 'error'>,
): string {
  const content = snapshot.assistant_content?.trim() || '';
  if (!['interrupted', 'failed'].includes(snapshot.status)) return content;

  const failure = snapshot.error?.trim()
    || (snapshot.status === 'interrupted'
      ? 'Generation was interrupted before the turn completed.'
      : 'The response failed before the turn completed.');
  if (!failure || content.includes(failure)) return content || failure;
  return content ? `${content}\n\n_${failure}_` : failure;
}

/**
 * The live bubble is never rewound. After a mid-turn reconnect the durable
 * snapshot the server replays can lag the chunks the client already painted
 * (soak D3: `turn_snapshot last_sequence=11` landing after live chunk 12), and
 * the durable gate keeps `lastSequence = max(...)`, so the overtaken chunk is
 * not replayed — replacing the bubble with the snapshot would lose it until
 * `complete`. A snapshot may therefore only ever *extend* what is painted.
 *
 * Returns `snapshot` when it continues the painted text (or nothing is painted
 * yet, the resume case this whole path exists for), and `painted` otherwise —
 * a shorter prefix or a divergent revision is a take-back, not an update.
 * Terminal snapshots are exempt: the persisted content is canonical for a
 * settled message, and its caller applies it directly.
 */
export function monotonicSnapshotText(painted: string, snapshot: string): string {
  if (!painted) return snapshot;
  if (!snapshot) return painted;
  return snapshot.startsWith(painted) ? snapshot : painted;
}
